import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import {
  Camera,
  Upload,
  FileText,
  X,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ShieldCheck,
  Gift,
  ExternalLink,
  Copy,
  Download,
  PenLine,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Calculator,
  ScrollText,
  Target,
  CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { MarianneHeader } from "../components/MarianneHeader";
import { analyzeFiles, analyzeExample, generateResponse, getConfig } from "../lib/api";
import { EXAMPLES } from "../lib/examples";
import { buildCalendarEvent, isOverdue, nextLocalMidnight, formatFrenchDate, parseFrenchDate } from "../lib/dates";
import { getBrowserQuota, incrementBrowserQuota } from "../lib/quota";
import { ErrorPanel } from "../components/ErrorPanel";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

async function renderPdfFirstPage(file, scale = 1.6) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  return { dataUrl: canvas.toDataURL("image/png"), numPages: pdf.numPages };
}

/* ------------------------- PDF branding helpers ------------------------- */
const PDF_NAVY = [11, 29, 81];

function drawPdfHeader(doc, title) {
  const W = doc.internal.pageSize.getWidth();
  const seg = W / 3;
  doc.setFillColor(0, 35, 149); doc.rect(0, 0, seg, 3, "F");
  doc.setFillColor(255, 255, 255); doc.rect(seg, 0, seg, 3, "F");
  doc.setFillColor(237, 41, 57); doc.rect(2 * seg, 0, seg, 3, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...PDF_NAVY);
  doc.text("Marianne", 15, 16);
  doc.setDrawColor(228, 228, 228); doc.setLineWidth(0.3); doc.line(15, 20, W - 15, 20);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...PDF_NAVY);
  doc.text(title, 15, 30);
  return 40;
}

function drawPdfFooter(doc) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setDrawColor(228, 228, 228); doc.setLineWidth(0.3); doc.line(15, H - 20, W - 15, H - 20);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(130, 130, 130);
  doc.text("Généré par Marianne. lamarianne.app", 15, H - 14);
  doc.text("Marianne vous aide à comprendre, elle ne remplace pas un conseil juridique.", 15, H - 9);
}

function Lightbox({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!item) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button className="absolute top-4 right-4 bg-white/15 rounded-full p-2" onClick={onClose} data-testid="lightbox-close">
        <X className="w-6 h-6 text-white" />
      </button>
      {item.kind === "pdf" ? (
        <iframe title="Aperçu PDF" src={item.src} className="w-full h-full max-w-3xl bg-white rounded-lg" onClick={(e) => e.stopPropagation()} />
      ) : (
        <img src={item.src} alt="Aperçu" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
      )}
    </motion.div>
  );
}

const scrollToTop = () => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); };


function buildLoadingMessages(docNoun, count = 1) {
  const noun = docNoun || "document";
  const plural = count > 1;
  const pl = (w) => (/[sx]$/i.test(w) ? w : `${w}s`);
  const lead = plural ? `Marianne lit vos ${pl(noun)}…` : `Marianne lit votre ${noun}…`;
  const jargon = plural ? `Décryptage du jargon de vos ${pl(noun)}…` : `Décryptage du jargon de votre ${noun}…`;
  return [
    lead,
    jargon,
    "Repérage des dates et montants…",
    "Vérification des actions à faire…",
  ];
}

function buildWritingMessages(analysis) {
  const isCommercial = analysis?.nature_document === "commercial";
  const isEmail = analysis?.response_type === "email";
  let first = "Marianne rédige votre réponse…";
  if (isCommercial) first = "Marianne rédige votre demande de rectification…";
  else if (isEmail) first = "Marianne rédige votre email…";
  return [
    first,
    "Choix du ton et des formules…",
    isEmail ? "Mise en forme de l'email…" : "Mise en forme du courrier…",
  ];
}

const REASSURANCE = [
  { icon: CheckCircle2, title: "Gratuit aujourd'hui", sub: "Sans compte" },
  { icon: ShieldCheck, title: "Documents supprimés", sub: "Après l'analyse" },
  { icon: Clock, title: "Réponse en 30\u00A0s", sub: "Prête à envoyer" },
];

const pageVariants = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function Footer({ navigate }) {
  return (
    <footer className="mt-auto px-5 py-6 text-center border-t border-gray-100">
      <button
        data-testid="footer-privacy-link"
        onClick={() => navigate("/confidentialite")}
        className="text-marianne underline underline-offset-4 font-medium text-base"
      >
        Confidentialité & protection de vos données
      </button>
      <p className="text-sm text-gray-400 mt-3">
        Marianne vous aide à comprendre, elle ne remplace pas un conseil juridique.
      </p>
    </footer>
  );
}

/* ------------------------- HOME ------------------------- */
function Home({ onCapture, onExample, navigate, quotaLimit, maxUploadBytes }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="flex flex-col min-h-[calc(100dvh-57px)]">
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
        <div className="min-w-0">
          <div className="px-5 pt-8 pb-4 flex flex-col">
            <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight text-marianne leading-tight" data-testid="home-title">
              Liberté, égalité,<br />
              <span className="text-marianne-blue">clarté.</span>
            </h1>
            <p className="flex justify-center text-lg text-gray-600 mt-3 leading-relaxed" data-testid="home-subtitle">
              Vos documents, enfin clairs, avec Marianne.
            </p>
            <p className="flex justify-center text-sm text-gray-400 mt-3 italic" data-testid="home-tagline">
              La République vous écrit. Marianne vous traduit.
            </p>
            <div className="flex justify-center mt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-gray-300 bg-white text-gray-600 text-xs">
                Assistant indépendant non affilié à l'administration
              </span>
            </div>
          </div>


          {/* Hidden inputs */}
          <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
            data-testid="camera-input"
            onChange={(e) => e.target.files.length && onCapture(Array.from(e.target.files))} />
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
            data-testid="file-input"
            onChange={(e) => e.target.files.length && onCapture(Array.from(e.target.files))} />

          <div className="px-5">
            <button
              data-testid="photograph-button"
              onClick={() => camRef.current?.click()}
              className="w-full min-h-[88px] rounded-3xl bg-marianne text-white flex items-center justify-center shadow-[0_10px_30px_rgba(11,29,81,0.35)] active:scale-[0.98] transition-transform"
            >
              <span className="font-head font-bold text-xl leading-9 text-balance">
                <Camera className="inline-block align-top mr-4 w-9 h-9" strokeWidth={2} />
                Photographier mon document
              </span>
            </button>

            <button
              data-testid="import-file-button"
              onClick={() => fileRef.current?.click()}
              className="w-full min-h-[56px] mt-3 rounded-2xl bg-white border-2 border-marianne/20 text-marianne flex items-center justify-center font-semibold text-lg active:scale-[0.98] transition-transform"
            >
              <span className="text-balance">
                <Upload className="inline-block align-top my-1 mr-3 w-5 h-5" />
                Importer un fichier (PDF, image)
              </span>
            </button>
            <p className="mt-2 text-sm text-gray-500" data-testid="upload-limit">{(maxUploadBytes / 1024 / 1024).toLocaleString("fr-FR")} Mo maximum au total.</p>
          </div>
        </div>

        <div className="min-w-0">
          {/* Reassurance */}
          <div className="px-5 mt-6 lg:mt-8 grid grid-cols-3 gap-3 items-stretch">
            {REASSURANCE.map((r, i) => (
              <div key={i} className="h-full bg-white rounded-2xl border border-gray-100 p-3 flex flex-col items-center text-center shadow-[0_4px_24px_rgba(11,29,81,0.05)]">
                <r.icon className="w-6 h-6 text-marianne-blue mb-1.5" />
                <span className="font-head font-bold text-sm text-marianne leading-tight [hyphens:none]" data-testid={i === 0 ? "home-quota" : undefined}>{i === 0 ? `Gratuit aujourd'hui : ${quotaLimit} analyses par jour` : r.title}</span>
                <span className="text-xs text-gray-500 mt-0.5 leading-tight">{r.sub}</span>
              </div>
            ))}
          </div>
          <p className="px-5 mt-3 text-base text-gray-500 leading-relaxed" data-testid="reassurance-tagline">
            Détecte les erreurs de montant, les clauses à risque et les échéances à ne pas manquer.
          </p>
          <div className="px-5 mt-8" id="examples-section" data-testid="examples-section">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-marianne-red" />
              <h2 className="font-head font-bold text-lg text-marianne">Essayez avec un exemple</h2>
            </div>
            <div className="space-y-3">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.id}
                  data-testid={`example-${ex.id}`}
                  onClick={() => onExample(ex.id)}
                  className="w-full bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-4 shadow-[0_4px_24px_rgba(11,29,81,0.05)] active:scale-[0.98] transition-transform text-left"
                >
                  <img src={ex.img} alt={ex.label} className="w-14 h-14 rounded-xl object-cover bg-gray-100 border border-gray-100" />
                  <div className="flex-1">
                    <div className="font-head font-bold text-marianne text-lg leading-tight">{ex.label}</div>
                    <div className="text-sm text-gray-500">{ex.org} : voir l'analyse en 1 clic</div>
                  </div>
                  <FileText className="w-5 h-5 text-marianne/40" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer navigate={navigate} />
    </motion.div>
  );
}

/* ------------------------- CAPTURE (multi-page review) ------------------------- */
function Capture({ initialFiles, onBack, onAnalyze }) {
  const [files, setFiles] = useState(initialFiles || []);
  const [situation, setSituation] = useState("");
  const [previews, setPreviews] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const addRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const built = files.map((f) => {
      const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
      if (isPdf) return { name: f.name, kind: "pdf", src: null, fileUrl: URL.createObjectURL(f) };
      return { name: f.name, kind: "image", src: URL.createObjectURL(f), fileUrl: null };
    });
    setPreviews(built);
    built.forEach((p, idx) => {
      if (p.kind === "pdf") {
        renderPdfFirstPage(files[idx])
          .then(({ dataUrl, numPages }) => {
            if (!cancelled) setPreviews((prev) => prev.map((x, i) => (i === idx ? { ...x, src: dataUrl, pages: numPages } : x)));
          })
          .catch(() => { /* garde la vignette PDF générique */ });
      }
    });
    return () => {
      cancelled = true;
      built.forEach((p) => {
        if (p.fileUrl) URL.revokeObjectURL(p.fileUrl);
        if (p.kind === "image" && p.src) URL.revokeObjectURL(p.src);
      });
    };
  }, [files]);

  const removeAt = (i) => setFiles(files.filter((_, idx) => idx !== i));
  const multiple = files.length > 1;

  const openLightbox = (p) => {
    if (p.kind === "pdf" && !p.src) setLightbox({ kind: "pdf", src: p.fileUrl });
    else setLightbox({ kind: "image", src: p.src });
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="flex flex-col min-h-[calc(100dvh-57px)] px-5 pt-5 lg:max-w-[30rem] lg:mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-marianne font-medium mb-4" data-testid="capture-back">
        <ArrowLeft className="w-5 h-5" /> Retour
      </button>
      <h2 className="font-head text-2xl font-bold text-marianne mb-1" data-testid="capture-title">{multiple ? "Vos documents" : "Votre document"}</h2>
      <p className="text-gray-600 mb-4">Vous pouvez ajouter plusieurs pages du même document. Touchez une vignette pour l'agrandir.</p>

      <input ref={addRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
        data-testid="capture-add-input"
        onChange={(e) => e.target.files.length && setFiles([...files, ...Array.from(e.target.files)])} />

      <div className="grid grid-cols-3 gap-3">
        {previews.map((p, i) => (
          <div key={i} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-gray-200 bg-white" data-testid={`preview-${i}`}>
            <button type="button" onClick={() => openLightbox(p)} className="w-full h-full" data-testid={`preview-open-${i}`}>
              {p.kind === "image" || p.src ? (
                <img src={p.src} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-marianne p-2">
                  <FileText className="w-8 h-8" />
                  <span className="text-xs mt-1 text-center break-all line-clamp-2">{p.name}</span>
                </div>
              )}
            </button>
            <button onClick={() => removeAt(i)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1" data-testid={`remove-${i}`}>
              <X className="w-4 h-4 text-white" />
            </button>
            {p.kind === "pdf" && p.pages > 1 && (
              <span className="absolute top-1 left-1 bg-marianne/90 text-white text-[10px] font-bold rounded px-1.5 py-0.5" data-testid={`pdf-pages-${i}`}>
                {p.pages} p.
              </span>
            )}
            <span className="absolute bottom-1 left-1 bg-marianne text-white text-xs font-bold rounded px-1.5">{i + 1}</span>
          </div>
        ))}
        <button onClick={() => addRef.current?.click()} data-testid="add-page-button"
          className="aspect-[3/4] rounded-xl border-2 border-dashed border-marianne/30 flex flex-col items-center justify-center text-marianne active:scale-95 transition-transform">
          <Upload className="w-7 h-7" />
          <span className="text-sm font-medium mt-1">Ajouter</span>
        </button>
      </div>

      <div className="mt-5">
        <label className="font-head font-semibold text-marianne flex items-center gap-2 mb-2" htmlFor="situation-field">
          <PenLine className="w-5 h-5" /> Précisez votre situation ou votre question (facultatif)
        </label>
        <textarea
          id="situation-field"
          data-testid="capture-situation-input"
          value={situation}
          onChange={(e) => setSituation(e.target.value)}
          placeholder="Ex : je ne comprends pas pourquoi je reçois ce courrier / puis-je contester ce montant ?"
          className="w-full rounded-xl border border-gray-200 p-3 text-base resize-none h-24 focus:outline-none focus:ring-2 focus:ring-marianne"
        />
      </div>

      <button
        data-testid="analyze-button"
        disabled={files.length === 0}
        onClick={() => onAnalyze(files, situation)}
        className="mt-4 w-full min-h-[64px] rounded-full bg-marianne text-white font-head font-bold text-xl flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(11,29,81,0.3)] disabled:opacity-40 active:scale-[0.98] transition-transform"
      >
        <Sparkles className="w-6 h-6" /> {multiple ? "Analyser mes documents" : "Analyser mon document"}
      </button>

      <AnimatePresence>
        {lightbox && <Lightbox item={lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </motion.div>
  );
}

/* ------------------------- ANALYZING / LOADER ------------------------- */
function RotatingLoader({ messages, testid = "loading-message" }) {
  const reducedMotion = useReducedMotion();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    const t = setInterval(() => {
      setIdx((i) => {
        if (i >= messages.length - 1) {
          clearInterval(t);
          return i;
        }
        return i + 1;
      });
    }, 2000);
    return () => clearInterval(t);
  }, [messages]);
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
      className="flex flex-col items-center justify-center min-h-[calc(100dvh-57px)] px-8 text-center">
      <motion.img
        src="/marianne-avatar.png"
        alt="Marianne"
        className="w-32 h-32 rounded-full border-4 border-marianne object-cover bg-white shadow-[0_10px_40px_rgba(11,29,81,0.25)]"
        animate={reducedMotion ? { scale: 1 } : { scale: [1, 1.06, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="mt-8 h-16 flex items-center" aria-live="polite">
        <AnimatePresence mode="wait">
          <motion.p
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="font-head text-xl font-semibold text-marianne"
            data-testid={testid}
          >
            {messages[idx]}
          </motion.p>
        </AnimatePresence>
      </div>
      <div className="flex gap-2 mt-4">
        {[0, 1, 2].map((d) => (
          <motion.span key={d} className="w-2.5 h-2.5 rounded-full bg-marianne-blue"
            animate={reducedMotion ? { opacity: 1 } : { opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: d * 0.2 }} />
        ))}
      </div>
      <p className="text-gray-400 text-sm mt-8">Vos documents sont supprimés dès l'analyse terminée.</p>
    </motion.div>
  );
}

function Analyzing({ docNoun, docCount = 1 }) {
  const messages = useMemo(() => buildLoadingMessages(docNoun, docCount), [docNoun, docCount]);
  return <RotatingLoader messages={messages} />;
}

function WritingLoader({ analysis }) {
  const messages = useMemo(() => buildWritingMessages(analysis), [analysis]);
  return <RotatingLoader messages={messages} testid="writing-message" />;
}


function QuotaReached({ limit, scope, resetAt, onBack, onExamples }) {
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
      className="flex flex-col items-center justify-center min-h-[calc(100dvh-57px)] px-8 text-center lg:max-w-xl lg:mx-auto" data-testid="quota-screen">
      <img src="/marianne-avatar.png" alt="Marianne"
        className="w-24 h-24 rounded-full border-4 border-marianne object-cover bg-white shadow-[0_10px_40px_rgba(11,29,81,0.2)]" />
      <h2 className="font-head text-2xl font-extrabold text-marianne mt-6">Vous avez fait le plein pour aujourd'hui</h2>
      <p className="text-lg text-gray-600 mt-3 leading-relaxed">
        La limite de {limit} analyses gratuites par jour {scope === "ip" ? "pour votre connexion" : "pour ce navigateur"} est atteinte.
        Les courriers d'exemple restent disponibles sans aucune limite.
      </p>
      <p className="text-lg text-gray-600 mt-3" data-testid="quota-reset">
        Vos analyses seront à nouveau disponibles le {formatFrenchDate(resetAt)}, à {resetAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} (heure locale de votre navigateur).
      </p>
      <button
        data-testid="quota-examples-button"
        onClick={onExamples}
        className="mt-6 text-marianne underline underline-offset-4 font-medium"
      >
        Voir les courriers d'exemple
      </button>
      <button
        data-testid="quota-home-button"
        onClick={onBack}
        className="mt-7 w-full min-h-[60px] rounded-full bg-marianne text-white font-head font-bold text-lg flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(11,29,81,0.3)] active:scale-[0.98] transition-transform"
      >
        Revenir à l'accueil
      </button>
    </motion.div>
  );
}

/* ------------------------- URGENCY ------------------------- */
const URGENCY = {
  aucune: { label: "Rien d'urgent à faire", icon: CheckCircle2, color: "#388E3C", bg: "#DCFCE7", border: "#388E3C" },
  moyenne: { label: "À traiter", icon: Clock, color: "#B45309", bg: "#FFEDD5", border: "#F57C00" },
  haute: { label: "Échéance importante", icon: AlertTriangle, color: "#B91C1C", bg: "#FEE2E2", border: "#D32F2F" },
};

const VIGILANCE = {
  faible: { label: "Faible", color: "#166534", bg: "#DCFCE7" },
  moyen: { label: "Moyen", color: "#B45309", bg: "#FFEDD5" },
  "élevé": { label: "Élevé", color: "#B91C1C", bg: "#FEE2E2" },
  eleve: { label: "Élevé", color: "#B91C1C", bg: "#FEE2E2" },
};

/* ------------------------- RESULT ------------------------- */
function Result({ data, onBack, onWriteResponse, navigate }) {
  const u = URGENCY[data.urgence] || URGENCY.aucune;
  const UIcon = u.icon;
  const hasAides = data.aides_potentielles && data.aides_potentielles.length > 0;
  const isScam = !!data.avertissement;
  const overdue = isOverdue(data.deadline);
  const rt = data.response_type || (data.reponse_necessaire ? "lettre" : "recap");
  const canWrite = rt === "lettre" || rt === "email" || !!data.example_id;
  const canRecap = (rt === "recap" || rt === "aucune") && (data.actions || []).length > 0;
  // À garder synchronisé avec les cinq enfants de la colonne de droite ci dessous.
  // Oublier une condition ici rend l'enfant correspondant invisible, sans erreur ni test rouge.
  const hasRightColumn = (data.actions && data.actions.length > 0) ||
    data.nature_document === "commercial" || canWrite || canRecap || hasAides;
  const deadlineDate = parseFrenchDate(data.deadline);
  const _todayStart = new Date(); _todayStart.setHours(0, 0, 0, 0);
  const futureDeadline = !!deadlineDate && deadlineDate.getTime() > _todayStart.getTime();

  const downloadIcs = () => downloadFile(buildCalendarEvent(data), "echeance-marianne.ics", "text/calendar;charset=utf-8");

  const downloadRecap = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    let y = drawPdfHeader(doc, "Récapitulatif des actions");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...PDF_NAVY);
    const head = doc.splitTextToSize(`${data.expediteur} : ${data.type_courrier}`, 180);
    doc.text(head, 15, y);
    y += head.length * 6 + 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    (data.actions || []).forEach((a, i) => {
      const lines = doc.splitTextToSize(`${i + 1}. ${a}`, 180);
      doc.text(lines, 15, y);
      y += lines.length * 6 + 2;
    });
    if (data.deadline) {
      y += 4;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...PDF_NAVY);
      doc.text(`Échéance : ${data.deadline}`, 15, y);
    }
    drawPdfFooter(doc);
    doc.save("recapitulatif-marianne.pdf");
    toast.success("Récapitulatif téléchargé !");
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="flex flex-col min-h-[calc(100dvh-57px)]">
      <div className="px-5 pt-5 pb-4 flex flex-col gap-4">
        <button onClick={onBack} className="flex items-center gap-2 text-marianne font-medium" data-testid="result-back">
          <ArrowLeft className="w-5 h-5" /> Analyser un autre document
        </button>

        <div>
          <span className="text-sm font-semibold uppercase tracking-wider text-marianne-blue">{data.type_courrier}</span>
          <h2 className="font-head text-2xl font-bold text-marianne leading-tight mt-0.5" data-testid="result-sender">{data.expediteur}</h2>
        </div>

        {data.status === "non_french_document" && (
          <p className="rounded-2xl p-4 bg-blue-50 text-marianne" data-testid="source-language">
            Langue du document détectée : {data.langue_source}. Marianne vous l'explique en français.
          </p>
        )}

        {isScam && (
          <div className="rounded-2xl bg-red-50 border-2 border-marianne-red p-4 flex gap-3" data-testid="scam-warning">
            <ShieldAlert className="w-7 h-7 text-marianne-red shrink-0" />
            <div>
              <div className="font-head font-bold text-marianne-red">⚠️ Attention, courrier suspect</div>
              <p className="text-red-900 mt-1 leading-relaxed">{data.avertissement}</p>
              <p className="text-red-800 text-sm mt-2 font-medium">Ne communiquez jamais vos coordonnées bancaires et ne payez rien sans vérifier.</p>
            </div>
          </div>
        )}
      </div>

      <div className={hasRightColumn
        ? "px-5 flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start"
        : "px-5 flex flex-col gap-4 lg:max-w-xl lg:mx-auto"}>
        <div className="min-w-0 flex flex-col gap-4">
          {/* Block 1 : En bref */}
          <section className="rounded-2xl p-5 bg-[#F0F4F8] border border-marianne/10" data-testid="block-resume">
            <h3 className="font-head font-bold text-lg text-marianne mb-2">En bref</h3>
            <p className="text-lg text-gray-800 leading-relaxed">{data.resume_simple}</p>
          </section>

          {/* Block 2 : Ce que ça veut dire */}
          <section className="rounded-2xl p-5 bg-white border border-gray-100 shadow-[0_4px_24px_rgba(11,29,81,0.05)]" data-testid="block-explanation">
            <h3 className="font-head font-bold text-lg text-marianne mb-2">Ce que ça veut dire pour vous</h3>
            <p className="text-lg text-gray-800 leading-relaxed">{data.explication_detaillee}</p>
          </section>

          {/* Block 3 : Urgence */}
          <section className="rounded-2xl p-5 bg-white border-l-8 shadow-[0_4px_24px_rgba(11,29,81,0.05)]"
            style={{ borderLeftColor: overdue ? "#F57C00" : u.border, backgroundColor: overdue ? "#FFEDD5" : u.bg }} data-testid="block-urgency">
            <div className="flex items-center gap-3">
              {overdue
                ? <Clock className="w-7 h-7" style={{ color: "#B45309" }} />
                : <UIcon className="w-7 h-7" style={{ color: u.color }} />}
              <div>
                <div className="font-head font-bold text-sm uppercase tracking-wider" style={{ color: overdue ? "#B45309" : u.color }}>Urgence</div>
                <div className="font-head font-bold text-xl" style={{ color: overdue ? "#B45309" : u.color }} data-testid="urgency-label">
                  {overdue ? "À régulariser" : u.label}
                </div>
              </div>
            </div>
            {data.deadline && (
              overdue ? (
                <div className="mt-3" data-testid="deadline">
                  <div className="font-semibold text-lg" style={{ color: "#0B1D51" }} data-testid="deadline-overdue">
                    Échéance dépassée le {data.deadline}
                  </div>
                  <p className="mt-1 text-base text-gray-600 leading-relaxed">
                    Pas de panique : il est souvent encore possible d'agir. Contactez l'organisme concerné dès que possible pour régulariser.
                  </p>
                </div>
              ) : (
                <div className="mt-3 font-semibold text-lg" style={{ color: "#0B1D51" }} data-testid="deadline">
                  {data.nature_document === "commercial" ? "Valable jusqu'au" : "À faire avant le"} {data.deadline}
                </div>
              )
            )}
          </section>

          {futureDeadline && (
            <button
              data-testid="add-calendar-button"
              onClick={downloadIcs}
              className="w-full min-h-[52px] rounded-full bg-white border-2 border-marianne text-marianne font-head font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <CalendarPlus className="w-5 h-5" /> Ajouter à mon calendrier
            </button>
          )}

          {data.page_count > 1 && (
            <section className="rounded-2xl p-5 bg-white border border-gray-100" data-testid="page-findings">
              <h3 className="font-head font-bold text-lg text-marianne mb-2">Les {data.page_count} pages de votre document</h3>
              <ul className="space-y-2 text-gray-800">
                {data.pages.map((page) => page.constats.map((finding, i) => (
                  <li key={`${page.page}-${i}`}>page {page.page} : {finding}</li>
                )))}
              </ul>
            </section>
          )}
        </div>

        {hasRightColumn && (
          <div className="min-w-0 flex flex-col gap-4">
            {/* Block 4 : Actions */}
            {data.actions && data.actions.length > 0 && (
              <section className="rounded-2xl p-5 bg-white border border-gray-100 shadow-[0_4px_24px_rgba(11,29,81,0.05)]" data-testid="block-actions">
                <h3 className="font-head font-bold text-lg text-marianne mb-3">Ce que vous devez faire</h3>
                {overdue && (
                  <div className="mb-3 rounded-xl bg-[#FFEDD5] text-[#8a4b00] p-3 leading-relaxed" data-testid="actions-overdue-note">
                    L'échéance est passée, mais il est souvent encore temps : traitez ces étapes en priorité et contactez l'organisme si besoin.
                  </div>
                )}
                <ol className="space-y-3">
                  {data.actions.map((a, i) => (
                    <li key={i} className="flex gap-3" data-testid={`action-${i}`}>
                      <span className="shrink-0 w-8 h-8 rounded-full bg-marianne text-white font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-lg text-gray-800 leading-relaxed pt-0.5">{a}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Vérification du document (documents commerciaux uniquement) */}
            {data.nature_document === "commercial" && (
              <section className="rounded-2xl p-5 bg-white border-2 border-marianne/15 shadow-[0_4px_24px_rgba(11,29,81,0.06)]" data-testid="block-verification">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-6 h-6 text-marianne-blue" />
                  <h3 className="font-head font-bold text-xl text-marianne">Vérification du document</h3>
                </div>

                {/* Contrôle des montants */}
                {data.controle_montants && (
                  <div className="mb-5" data-testid="controle-montants">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="w-5 h-5 text-marianne" />
                      <h4 className="font-head font-bold text-lg text-marianne">Contrôle des montants</h4>
                    </div>
                    <div
                      className="rounded-xl p-3 flex items-center gap-2 font-semibold"
                      style={data.controle_montants.coherent
                        ? { backgroundColor: "#DCFCE7", color: "#166534" }
                        : { backgroundColor: "#FEE2E2", color: "#991B1B" }}
                      data-testid="montants-status"
                    >
                      {data.controle_montants.coherent
                        ? <><CheckCircle2 className="w-5 h-5" /> Les montants semblent cohérents</>
                        : <><AlertTriangle className="w-5 h-5" /> Incohérence détectée dans les montants</>}
                    </div>
                    {data.controle_montants.anomalies && data.controle_montants.anomalies.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {data.controle_montants.anomalies.map((an, i) => (
                          <li key={i} className="flex gap-2 text-gray-800 leading-relaxed" data-testid={`anomalie-${i}`}>
                            <span className="text-marianne-blue font-bold mt-0.5">•</span>
                            <span>{an}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Clauses à surveiller */}
                {data.clauses_risque && data.clauses_risque.length > 0 && (
                  <div className="mb-5" data-testid="clauses-risque">
                    <div className="flex items-center gap-2 mb-3">
                      <ScrollText className="w-5 h-5 text-marianne" />
                      <h4 className="font-head font-bold text-lg text-marianne">Clauses à surveiller</h4>
                    </div>
                    <div className="space-y-3">
                      {data.clauses_risque.map((c, i) => {
                        const v = VIGILANCE[(c.vigilance || "faible").toLowerCase()] || VIGILANCE.faible;
                        return (
                          <div key={i} className="rounded-xl border border-gray-100 p-3 bg-gray-50" data-testid={`clause-${i}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-head font-bold text-marianne">{c.nom}</span>
                              <span className="text-xs font-bold uppercase tracking-wider rounded-full px-2.5 py-1 shrink-0"
                                style={{ backgroundColor: v.bg, color: v.color }} data-testid={`clause-vigilance-${i}`}>
                                {v.label}
                              </span>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{c.implication}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Points à négocier */}
                {data.points_negociation && data.points_negociation.length > 0 && (
                  <div data-testid="points-negociation">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-5 h-5 text-marianne" />
                      <h4 className="font-head font-bold text-lg text-marianne">Ce qu'il faut négocier avant de signer</h4>
                    </div>
                    <ol className="space-y-2">
                      {data.points_negociation.map((p, i) => (
                        <li key={i} className="flex gap-3" data-testid={`negociation-${i}`}>
                          <span className="shrink-0 w-7 h-7 rounded-full bg-marianne-blue text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                          <span className="text-gray-800 leading-relaxed pt-0.5">{p}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </section>
            )}

            {/* Réponse adaptée au type de document */}
            {canWrite && (
              <button
                data-testid="write-response-button"
                onClick={onWriteResponse}
                className="w-full min-h-[64px] rounded-full bg-marianne text-white font-head font-bold text-xl flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(11,29,81,0.3)] active:scale-[0.98] transition-transform"
              >
                <PenLine className="w-6 h-6" /> Rédiger ma réponse
              </button>
            )}
            {canRecap && (
              <button
                data-testid="download-recap-button"
                onClick={downloadRecap}
                className="w-full min-h-[64px] rounded-full bg-marianne text-white font-head font-bold text-xl flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(11,29,81,0.3)] active:scale-[0.98] transition-transform"
              >
                <Download className="w-6 h-6" /> Télécharger le récapitulatif des actions
              </button>
            )}

            {/* Bonus : aides */}
            {hasAides && (
              <section className="rounded-2xl p-5 bg-[#FFFBEB] border-2 border-dashed border-amber-300" data-testid="block-aides">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-6 h-6 text-amber-600" />
                  <h3 className="font-head font-bold text-lg text-amber-800">Au fait, vous avez peut-être droit à…</h3>
                </div>
                <div className="space-y-3">
                  {data.aides_potentielles.map((aide, i) => (
                    <div key={i} className="bg-white rounded-xl p-3 border border-amber-100" data-testid={`aide-${i}`}>
                      <div className="font-head font-bold text-marianne">{aide.nom}</div>
                      {aide.condition && <div className="text-gray-600 text-sm mt-0.5">{aide.condition}</div>}
                      {aide.lien_officiel && (
                        <a href={aide.lien_officiel} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-marianne-blue font-semibold text-sm mt-2 underline underline-offset-2">
                          Voir la démarche officielle <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <div className="px-5 pt-4 pb-4">
        <p className="text-sm text-gray-400 italic text-center mt-2" data-testid="disclaimer">{data.disclaimer}</p>
      </div>
      <Footer navigate={navigate} />
    </motion.div>
  );
}

/* ------------------------- RESPONSE (letter / email) ------------------------- */
const FIELDS_KEY = "marianne_fields_v1";
const FR_MONTH_NAMES = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
  "août", "septembre", "octobre", "novembre", "décembre"];
function frToday() {
  const d = new Date();
  return `${d.getDate()} ${FR_MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

/* ------------------------- RESPONSE (letter) ------------------------- */
function downloadFile(content, filename, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Response({ analysis, onBack }) {
  const [template, setTemplate] = useState("");
  const [letter, setLetter] = useState("");
  const [loading, setLoading] = useState(true);
  const [situation, setSituation] = useState("");
  const [fields, setFields] = useState({});

  const isEmail = analysis?.response_type === "email";

  const tokens = useMemo(
    () => Array.from(new Set((template.match(/\[[^\]]+\]/g) || []))),
    [template]
  );
  const [error, setError] = useState(false);
  const requestVersion = useRef(0);
  const lastSituation = useRef("");

  const load = useCallback(async (sit = "") => {
    const version = ++requestVersion.current;
    lastSituation.current = sit;
    setLoading(true);
    setError(false);
    try {
      const l = await generateResponse(analysis, sit);
      if (version !== requestVersion.current) return;
      setTemplate(l);
      setLetter(l);
    } catch (e) {
      if (version === requestVersion.current) setError(true);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [analysis]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);
  useLayoutEffect(scrollToTop, [loading, error]);

  // Pré-remplissage des champs détectés (localStorage + date du jour pour [Date])
  useEffect(() => {
    if (!template) return;
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(FIELDS_KEY) || "{}"); } catch { /* ignore */ }
    const init = {};
    tokens.forEach((t) => {
      let v = saved[t] || "";
      if (!v && /^\[Date\]$/i.test(t)) v = frToday();
      init[t] = v;
    });
    setFields(init);
    // eslint-disable-next-line
  }, [template]);

  const insertFields = () => {
    let out = template;
    Object.entries(fields).forEach(([tok, val]) => {
      if (val && val.trim()) out = out.split(tok).join(val.trim());
    });
    setLetter(out);
    try { localStorage.setItem(FIELDS_KEY, JSON.stringify(fields)); } catch { /* ignore */ }
    toast.success("Informations insérées !");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(letter);
      toast.success(isEmail ? "Email copié !" : "Lettre copiée !");
    } catch {
      toast.error("Sélectionnez le texte de la lettre pour le copier, ou téléchargez le PDF.");
    }
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const H = doc.internal.pageSize.getHeight();
    const bottom = H - 26;
    const headerTitle = isEmail ? "Votre email" : "Votre réponse";
    let y = drawPdfHeader(doc, headerTitle);
    doc.setFont("courier", "normal");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    const lines = doc.splitTextToSize(letter, 180);
    lines.forEach((ln) => {
      if (y > bottom) {
        drawPdfFooter(doc);
        doc.addPage();
        y = drawPdfHeader(doc, headerTitle);
        doc.setFont("courier", "normal");
        doc.setFontSize(11);
        doc.setTextColor(30, 30, 30);
      }
      doc.text(ln, 15, y);
      y += 6;
    });
    drawPdfFooter(doc);
    doc.save(isEmail ? "email-marianne.pdf" : "reponse-marianne.pdf");
    toast.success("PDF téléchargé !");
  };

  const regenerate = () => {
    load(situation);
  };

  if (loading) return <WritingLoader analysis={analysis} />;
  if (error) return <ErrorPanel onRetry={() => load(lastSituation.current)} onBack={onBack} />;

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="flex flex-col min-h-[calc(100dvh-57px)] px-5 pt-5">
      <button onClick={onBack} className="flex items-center gap-2 text-marianne font-medium mb-4" data-testid="response-back">
        <ArrowLeft className="w-5 h-5" /> Retour à l'analyse
      </button>
      <h2 className="font-head text-2xl font-bold text-marianne mb-1">{isEmail ? "Votre email est prêt" : "Votre réponse est prête"}</h2>
      <p className="text-gray-600 mb-4">Complétez les champs entre [crochets], puis copiez ou téléchargez.</p>

      <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
        <div className="min-w-0">
          <div className="rounded-2xl bg-white border border-gray-200 p-4 shadow-[0_4px_24px_rgba(11,29,81,0.06)]">
            <pre className="font-letter text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed" data-testid="letter-content">{letter}</pre>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <button onClick={copy} data-testid="copy-button"
              className="min-h-[56px] rounded-full bg-marianne text-white font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Copy className="w-5 h-5" /> Copier
            </button>
            <button onClick={downloadPdf} data-testid="download-button"
              className="min-h-[56px] rounded-full bg-white border-2 border-marianne text-marianne font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Download className="w-5 h-5" /> PDF
            </button>
          </div>
        </div>

        <div className="min-w-0 lg:flex lg:flex-col lg:gap-5">
          {tokens.length > 0 && (
            <div className="mt-5 lg:mt-0 rounded-2xl bg-white border border-marianne/15 p-4 shadow-[0_4px_24px_rgba(11,29,81,0.06)]" data-testid="personalize-panel">
              <label className="font-head font-bold text-marianne flex items-center gap-2 mb-1">
                <PenLine className="w-5 h-5" /> Complétez vos informations
              </label>
              <p className="text-sm text-gray-500 mb-3">Ces informations restent sur votre appareil et ne sont jamais envoyées.</p>
              <div className="space-y-3">
                {tokens.map((t, i) => (
                  <div key={t}>
                    <label className="text-sm font-medium text-gray-600 block mb-1">{t.replace(/^\[|\]$/g, "")}</label>
                    <input
                      data-testid={`field-input-${i}`}
                      value={fields[t] || ""}
                      onChange={(e) => setFields((p) => ({ ...p, [t]: e.target.value }))}
                      className="w-full rounded-xl border border-gray-200 p-2.5 text-base focus:outline-none focus:ring-2 focus:ring-marianne"
                    />
                  </div>
                ))}
              </div>
              <button
                data-testid="insert-fields-button"
                onClick={insertFields}
                className="mt-4 w-full min-h-[52px] rounded-full bg-marianne text-white font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <PenLine className="w-5 h-5" /> Insérer mes informations
              </button>
            </div>
          )}

          <div className="mt-5 lg:mt-0 rounded-2xl bg-[#F0F4F8] border border-marianne/10 p-4">
            <label className="font-head font-bold text-marianne flex items-center gap-2 mb-2">
              <PenLine className="w-5 h-5" /> Préciser ma situation
            </label>
            <textarea
              data-testid="situation-input"
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              placeholder="Ex : je conteste ce montant car j'ai déjà payé…"
              className="w-full rounded-xl border border-gray-200 p-3 text-base resize-none h-24 focus:outline-none focus:ring-2 focus:ring-marianne"
            />
            <button onClick={regenerate} disabled={loading} data-testid="regenerate-button"
              className="mt-2 w-full min-h-[52px] rounded-full bg-marianne-blue text-white font-head font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-transform">
              <RefreshCw className="w-5 h-5" /> Régénérer la réponse
            </button>
          </div>
        </div>
      </div>
      <div className="h-8" />
    </motion.div>
  );
}

/* ------------------------- MAIN FLOW ------------------------- */
const DOCUMENT_ERRORS = {
  unreadable_document: { title: "Le document est difficile à lire", message: "Je n'arrive pas à lire ce document. Rapprochez-vous, évitez les reflets, et reprenez la photo." },
  unsupported_document: { title: "Ce document n'est pas pris en charge", message: "Marianne explique les courriers administratifs, les devis, les contrats et les bons de commande. Choisissez l'un de ces documents." },
  file_too_large: { title: "Le fichier est trop volumineux" },
  invalid_pdf: { title: "Le PDF ne peut pas être lu" },
  unsupported_file: { title: "Ce format n'est pas pris en charge" },
  too_many_pages: { title: "Ce document contient trop de pages" },
};

export default function Marianne() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [screen, setScreen] = useState("home");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [docNoun, setDocNoun] = useState("document");
  const [docCount, setDocCount] = useState(1);
  const [config, setConfig] = useState({ quota_navigateur: 10, quota_ip: 10, max_upload_bytes: 10 * 1024 * 1024 });
  const [quota, setQuota] = useState(null);
  const [issue, setIssue] = useState(null);
  const retry = useRef(null);
  const requestVersion = useRef(0);

  useLayoutEffect(scrollToTop, [screen]);
  useEffect(() => {
    let active = true;
    getConfig().then((data) => {
      if (!active) return;
      setConfig((previous) => {
        const next = { ...previous };
        Object.keys(previous).forEach((key) => {
          if (Number.isInteger(data[key]) && data[key] >= 0) next[key] = data[key];
        });
        return next;
      });
    }).catch(() => {});
    return () => { active = false; requestVersion.current += 1; };
  }, []);

  const goHome = useCallback(() => {
    requestVersion.current += 1;
    setScreen("home");
    setAnalysis(null);
    setPendingFiles([]);
    setIssue(null);
    retry.current = null;
  }, []);

  useEffect(() => {
    if (screen !== "quota" || !quota) return;
    const timer = setTimeout(goHome, Math.max(0, quota.resetAt.getTime() - Date.now()));
    return () => clearTimeout(timer);
  }, [screen, quota, goHome]);

  const showFailure = (error, action) => {
    const body = error?.response?.data;
    const detail = body?.detail && typeof body.detail === "object" ? body.detail : body;
    if (error?.response?.status === 429) {
      const reset = new Date(detail?.reset_at);
      setQuota({
        limit: Number.isInteger(detail?.limit) ? detail.limit : config.quota_ip,
        scope: "ip", resetAt: Number.isNaN(reset.getTime()) ? nextLocalMidnight() : reset,
      });
      setScreen("quota");
      return;
    }
    const status = detail?.status || (error?.response?.status === 413 ? "file_too_large" : null);
    if (DOCUMENT_ERRORS[status]) {
      const fallback = status === "file_too_large"
        ? "La limite est de " + (config.max_upload_bytes / 1024 / 1024).toLocaleString("fr-FR") + " Mo au total. Choisissez une photo plus légère."
        : "Choisissez une nouvelle photo ou un PDF lisible.";
      setIssue({ ...DOCUMENT_ERRORS[status], message: detail?.message || DOCUMENT_ERRORS[status].message || fallback });
      setScreen("document-error");
      return;
    }
    retry.current = action;
    setScreen("unavailable");
  };

  const runAnalysis = async (files, situation) => {
    const version = ++requestVersion.current;
    setPendingFiles(files);
    if (files.reduce((total, file) => total + file.size, 0) > config.max_upload_bytes) {
      showFailure({ response: { status: 413 } });
      return;
    }
    if (getBrowserQuota().count >= config.quota_navigateur) {
      setQuota({ limit: config.quota_navigateur, scope: "browser", resetAt: nextLocalMidnight() });
      setScreen("quota");
      return;
    }
    setDocNoun("document");
    setDocCount(files.length);
    setScreen("analyzing");
    try {
      const data = await analyzeFiles(files, situation);
      if (version !== requestVersion.current) return;
      incrementBrowserQuota();
      setAnalysis(data);
      setScreen("result");
    } catch (error) {
      if (version === requestVersion.current) showFailure(error, () => runAnalysis(files, situation));
    }
  };

  const runExample = async (id) => {
    const version = ++requestVersion.current;
    setDocNoun(EXAMPLES.find((example) => example.id === id)?.doc || "document");
    setDocCount(1);
    setScreen("analyzing");
    try {
      const data = await analyzeExample(id);
      if (version !== requestVersion.current) return;
      setAnalysis(data);
      setScreen("result");
    } catch (error) {
      if (version === requestVersion.current) showFailure(error, () => runExample(id));
    }
  };

  const goToExamples = () => {
    goHome();
    setTimeout(() => {
      document.getElementById("examples-section")?.scrollIntoView({ behavior: reducedMotion ? "instant" : "smooth", block: "start" });
    }, 400);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="marianne-shell flex flex-col">
        <MarianneHeader onLogoClick={goHome} />
        <AnimatePresence mode="wait" onExitComplete={scrollToTop}>
          {screen === "home" && (
            <Home key="home" navigate={navigate} quotaLimit={config.quota_navigateur} maxUploadBytes={config.max_upload_bytes}
              onCapture={(files) => { setPendingFiles(files); setScreen("capture"); }}
              onExample={runExample} />
          )}
          {screen === "capture" && (
            <Capture key="capture" initialFiles={pendingFiles} onBack={goHome} onAnalyze={runAnalysis} />
          )}
          {screen === "analyzing" && <Analyzing key="analyzing" docNoun={docNoun} docCount={docCount} />}
          {screen === "result" && analysis && (
            <Result key="result" data={analysis} navigate={navigate}
              onBack={goHome} onWriteResponse={() => setScreen("response")} />
          )}
          {screen === "response" && analysis && (
            <Response key="response" analysis={analysis} onBack={() => setScreen("result")} />
          )}
          {screen === "quota" && quota && (
            <QuotaReached key="quota" {...quota} onBack={goHome} onExamples={goToExamples} />
          )}
          {screen === "unavailable" && (
            <ErrorPanel key="unavailable" onRetry={() => retry.current?.()}
              onBack={pendingFiles.length ? () => setScreen("capture") : goHome} />
          )}
          {screen === "document-error" && (
            <ErrorPanel key="document-error" issue={issue} onBack={goHome} />
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
}
